import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Onglet Cohérence v2 — liste actionnable « pièces à demander au parquet ».
 *
 * Affiche les références (PV / pièce / cote / audition) citées dans
 * le texte OCR du dossier mais ABSENTES de la liste des pièces
 * réellement versées. Regroupement par identifiant canonique (les
 * variantes OCR du même PV sont collapsées).
 *
 * Action principale : "Copier la liste pour le parquet" — formatte
 * un texte Markdown prêt à coller dans un email / requête.
 */

type Finding = {
  canonical_key?: string;
  type: 'missing_piece' | 'unresolved_ref';
  ref_type: string | null;
  ref_raw: string;
  ref_normalized: Record<string, unknown> | null;
  variants?: string[];
  cited_in: { pdf_id: string; page: number; source: string }[];
  occurrences_count: number;
  confidence: number;
  status: 'unverified' | 'ignored' | 'requested';
};

type Report = {
  report_id: string;
  generated_at: string;
  findings: Finding[];
} | null;

type Props = { dossierId: string };

const SECTIONS: { id: string; label: string; ref_types: string[] }[] = [
  { id: 'pv', label: 'PV cités sans correspondance', ref_types: ['pv'] },
  { id: 'piece', label: 'Annexes / pièces mentionnées sans correspondance', ref_types: ['piece'] },
  { id: 'audition', label: 'Auditions citées sans correspondance', ref_types: ['audition'] },
  { id: 'cote', label: 'Cotes orphelines', ref_types: ['cote', 'carton_sf'] },
  { id: 'autre', label: 'Autres références', ref_types: [] },
];

export default function CoherenceTab({ dossierId }: Props) {
  const [report, setReport] = useState<Report>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const loadLast = async () => {
    setLoading(true);
    try {
      const r = await api.get<Report>(`/dossiers/${dossierId}/coherence/last-report`);
      setReport(r);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 404) {
        toast.error(
          err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
        );
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const scan = async () => {
    if (
      !window.confirm(
        'Lancer une analyse de cohérence du dossier ?\n\n' +
          "L'opération extrait les références (PV, pièces, cotes, auditions) " +
          'citées dans le texte OCR de toutes les pièces et les croise avec les ' +
          'pièces réellement présentes. Coût : gratuit (regex strictes uniquement).'
      )
    )
      return;
    setScanning(true);
    try {
      const r = await api.post<Report>(`/dossiers/${dossierId}/coherence/scan`);
      setReport(r);
      toast.success('Analyse terminée');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setScanning(false);
    }
  };

  // Regroupement par section.
  const grouped = useMemo(() => {
    if (!report) return {} as Record<string, Finding[]>;
    const map: Record<string, Finding[]> = {};
    for (const s of SECTIONS) map[s.id] = [];
    const fallbackId = 'autre';
    for (const f of report.findings) {
      const sec = SECTIONS.find((s) => s.ref_types.includes(f.ref_type ?? ''));
      const bucket = sec ? map[sec.id] : map[fallbackId];
      if (bucket) bucket.push(f);
    }
    return map;
  }, [report]);

  const totalFindings = report?.findings.length ?? 0;

  const copyForParquet = () => {
    if (!report) return;
    const lines: string[] = [];
    lines.push(`# Cohérence dossier — pièces à demander\n`);
    lines.push(`_Analyse du ${new Date(report.generated_at).toLocaleDateString('fr-BE')}_`);
    lines.push('');
    for (const s of SECTIONS) {
      const items = grouped[s.id];
      if (!items || items.length === 0) continue;
      lines.push(`## ${s.label} (${items.length})`);
      lines.push('');
      for (const f of items) {
        const label = formatCanonical(f);
        const cite = f.occurrences_count === 1 ? '1 mention' : `${f.occurrences_count} mentions`;
        lines.push(`- **${label}** _(${cite})_`);
        if (f.variants && f.variants.length > 1) {
          lines.push(`  - Variantes OCR : ${f.variants.map((v) => `« ${v} »`).join(', ')}`);
        }
      }
      lines.push('');
    }
    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success('Liste copiée dans le presse-papier'),
      () => toast.error('Copie impossible — sélectionne manuellement')
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-rule bg-paper-elevated p-4 text-[13px] leading-relaxed">
        <p className="text-text-secondary">
          Cet outil parcourt le texte OCR de toutes les pièces du dossier et liste les{' '}
          <strong className="text-text-primary">PV, annexes, cotes et auditions cités</strong> dans
          le texte mais qui ne correspondent à aucune pièce réellement versée. Sert à préparer la
          liste des <strong className="text-text-primary">pièces à demander au parquet</strong>.
        </p>
      </div>

      <div className="flex items-center justify-between rounded-md border border-rule bg-paper-elevated p-3">
        <div className="text-[12px] text-text-secondary">
          {loading ? (
            'Chargement…'
          ) : report ? (
            <>
              Dernière analyse :{' '}
              <strong className="font-medium text-text-primary">
                {new Date(report.generated_at).toLocaleString('fr-BE')}
              </strong>
              {' · '}
              <strong className="font-medium text-text-primary">{totalFindings}</strong> référence
              {totalFindings > 1 ? 's' : ''} à clarifier
            </>
          ) : (
            'Aucune analyse pour ce dossier. Lance la première vérification.'
          )}
        </div>
        <div className="flex items-center gap-2">
          {report && totalFindings > 0 && (
            <Btn variant="secondary" size="sm" onClick={copyForParquet}>
              📋 Copier la liste
            </Btn>
          )}
          <Btn variant="primary" size="sm" onClick={() => void scan()} disabled={scanning}>
            {scanning ? 'Analyse en cours…' : '↻ Vérifier cohérence'}
          </Btn>
        </div>
      </div>

      {report && totalFindings === 0 && (
        <div className="rounded-md border border-cat-fav/40 bg-cat-fav-pastel p-4 text-[13px] text-cat-fav">
          ✓ Aucune incohérence détectée. Toutes les références citées dans le texte OCR
          correspondent à une pièce ou une entrée d'inventaire du dossier.
        </div>
      )}

      {report &&
        totalFindings > 0 &&
        SECTIONS.map((section) => {
          const items = grouped[section.id];
          if (!items || items.length === 0) return null;
          return (
            <section key={section.id}>
              <h3 className="mb-2 flex items-center gap-2 text-[14px] font-medium text-text-primary">
                <span>{section.label}</span>
                <span className="rounded-pill bg-paper-subtle px-2 py-[1px] text-[11px] font-normal text-text-secondary">
                  {items.length}
                </span>
              </h3>
              <ul className="space-y-1.5">
                {items.map((f, i) => (
                  <FindingCard
                    key={`${f.canonical_key || f.ref_raw}-${i}`}
                    dossierId={dossierId}
                    finding={f}
                  />
                ))}
              </ul>
            </section>
          );
        })}

      <p className="text-[10px] text-text-tertiary">
        Analyse via regex strictes uniquement (Haiku IA désactivé par défaut — trop de faux
        positifs). Les pages doivent être OCRisées pour être analysées. Pour activer l'IA fallback :{' '}
        <code>?use_ai=true</code> sur l'endpoint scan.
      </p>
    </div>
  );
}

function formatCanonical(f: Finding): string {
  const norm = f.ref_normalized || {};
  if (f.ref_type === 'pv' && typeof norm['pv_numero'] === 'string') {
    return `PV n° ${norm['pv_numero']}`;
  }
  if (f.ref_type === 'piece' && norm['piece'] != null) {
    return `Pièce n° ${norm['piece']}`;
  }
  if (f.ref_type === 'cote' && norm['cote'] != null) {
    return `Cote ${norm['cote']}`;
  }
  if (f.ref_type === 'carton_sf') {
    return `Carton ${norm['carton'] ?? '?'} — sous-farde ${norm['sous_farde'] ?? '?'}`;
  }
  if (f.ref_type === 'audition') {
    const name = norm['person_name'];
    const date = norm['date_raw'];
    if (typeof name === 'string' && typeof date === 'string') {
      return `Audition de ${name} du ${date}`;
    }
  }
  return f.ref_raw;
}

function FindingCard({ dossierId, finding: f }: { dossierId: string; finding: Finding }) {
  const [expanded, setExpanded] = useState(false);
  const canonical = formatCanonical(f);
  return (
    <li className="rounded-md border border-cat-imp/30 bg-cat-imp-pastel p-2.5 text-[12px]">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-text-primary">{canonical}</p>
          {f.variants && f.variants.length > 1 && (
            <p className="mt-0.5 text-[10px] text-text-tertiary">
              Variantes OCR :{' '}
              {f.variants
                .slice(0, 3)
                .map((v) => `« ${v} »`)
                .join(', ')}
              {f.variants.length > 3 && ` (+${f.variants.length - 3})`}
            </p>
          )}
        </div>
        <span className="shrink-0 font-mono text-[10px] text-text-tertiary">
          {f.occurrences_count} mention{f.occurrences_count > 1 ? 's' : ''}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="mt-1 cursor-pointer text-[11px] text-text-secondary hover:text-text-primary"
      >
        {expanded ? '▾' : '▸'} Voir occurrences ({f.cited_in.length})
      </button>
      {expanded && (
        <ul className="mt-1 space-y-0.5 pl-3 text-[11px]">
          {f.cited_in.map((c, j) => (
            <li key={j}>
              <Link
                to={`/dossiers/${dossierId}/pieces/${c.pdf_id}?page=${c.page}`}
                className="text-accent-text no-underline hover:underline"
              >
                Pièce {c.pdf_id.slice(0, 8)} · page {c.page}
              </Link>{' '}
              <span className="text-text-tertiary">({c.source})</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
